/**
 * useHasNoSuikamon — the app-wide "this visitor owns zero Suikamon" signal.
 *
 * Every onboarding surface (sticky play banner, inline CTA cards, welcome popup) asks this
 * hook instead of re-deriving the answer, so a player who already has a pet never sees a
 * "start playing" prompt anywhere.
 *
 * Gated on the acting IDENTITY (`actingAddress` = login identity, Sui wallet, or guest
 * sentinel) — never on a Sui wallet, per CLAUDE.md identity-vs-custody. A visitor with no
 * identity at all has no Suikamon by definition, so the hook answers `true` without a
 * network call. While the has-pets lookup is in flight the hook answers `false`, so the
 * prompt never flashes at a player who does own pets.
 */
import { useQuery } from '@tanstack/react-query';
import { checkUserHasAnyPets } from '../api/petBackendApi';
import { useWalletConnection } from '../auth/useWalletConnection';

/** Query-key root for the has-pets lookup — invalidate it the moment a pet is created. */
export const HAS_ANY_PETS_QUERY_KEY = 'hasAnyPets';

/** Has-pets rarely flips; keep it cached so every mounted CTA shares one request. */
const HAS_PETS_STALE_MS = 30_000;

export function useHasNoSuikamon(): boolean {
  const { actingAddress } = useWalletConnection();

  const { data: hasPets } = useQuery({
    queryKey: [HAS_ANY_PETS_QUERY_KEY, actingAddress],
    queryFn: () => checkUserHasAnyPets(actingAddress!),
    enabled: !!actingAddress,
    staleTime: HAS_PETS_STALE_MS,
  });

  if (!actingAddress) return true;
  return hasPets === false;
}
